import React, { useState, useEffect, useCallback } from 'react';
import api from '../config';
import {
  ClipboardList, Upload, Loader2, ChevronDown, ChevronRight, Trash2, AlertCircle,
  Link2, X, FolderOpen, Sparkles,
} from 'lucide-react';

const STATUSES = [
  { value: 'requested', label: 'Requested', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
  { value: 'received', label: 'Received', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  { value: 'reviewed', label: 'Reviewed', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  { value: 'flagged', label: 'Flagged', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
  { value: 'not_applicable', label: 'N/A', cls: 'bg-slate-50 text-slate-400 border-slate-200' },
];

const DOMAIN_LABELS = {
  financials: 'Financials',
  hr: 'HR',
  billing: 'Billing / AR',
  crm: 'CRM',
  unmapped: 'Unmapped',
};

const statusMeta = (v) => STATUSES.find((s) => s.value === v) || STATUSES[0];

/**
 * Counterparty DD request list for one deal: import a numbered list
 * (PDF / DOCX / TXT or pasted text), then track each item's status.
 * Items come only from the imported document; the agent-domain tag is a
 * suggestion the user can change.
 */
export default function DDRequestList({ dealId }) {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(true);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [noteDraft, setNoteDraft] = useState('');
  const [room, setRoom] = useState(null); // { files, suggestions }
  const [attachOpen, setAttachOpen] = useState(null);
  const [markReceived, setMarkReceived] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/api/dd/deals/${dealId}/requests`);
      setDoc(r.data);
      setError('');
    } catch (e) {
      if (e.response?.status === 404) setDoc(null);
      else setError(e.response?.data?.detail || 'Could not load the request list.');
    } finally {
      setLoading(false);
    }
  }, [dealId]);

  useEffect(() => { load(); }, [load]);

  const loadRoom = useCallback(async () => {
    try {
      const r = await api.get(`/api/dd/deals/${dealId}/requests/data-room`);
      setRoom(r.data);
    } catch { /* 404 or no rooms — panel just stays hidden */ }
  }, [dealId]);

  useEffect(() => { loadRoom(); }, [loadRoom]);

  const importList = async ({ file, text }) => {
    setBusy(true);
    setError('');
    try {
      const fd = new FormData();
      if (file) fd.append('file', file);
      if (text) fd.append('text', text);
      if (doc) fd.append('replace', 'true');
      const r = await api.post(`/api/dd/deals/${dealId}/requests`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setDoc(r.data);
      setPasteOpen(false);
      setPasteText('');
    } catch (e) {
      setError(e.response?.data?.detail || 'Import failed.');
    } finally {
      setBusy(false);
    }
  };

  const update = async (itemId, patch) => {
    setError('');
    try {
      const r = await api.put(`/api/dd/deals/${dealId}/requests/${itemId}`, patch);
      setDoc(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Update failed.');
    }
  };

  const linkFile = async (itemId, file, doMarkReceived) => {
    setError('');
    try {
      const r = await api.put(`/api/dd/deals/${dealId}/requests/${itemId}/link`, {
        room_id: file.room_id,
        filename: file.filename,
        mark_received: doMarkReceived,
      });
      setDoc(r.data);
      setAttachOpen(null);
      loadRoom();
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not link that file.');
    }
  };

  const unlinkFile = async (itemId, lf) => {
    setError('');
    try {
      const r = await api.delete(
        `/api/dd/deals/${dealId}/requests/${itemId}/link`,
        { params: { room_id: lf.room_id, filename: lf.filename } },
      );
      setDoc(r.data);
      loadRoom();
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not unlink that file.');
    }
  };

  const remove = async () => {
    if (!window.confirm('Remove this request list? Status tracking for these items will be lost.')) return;
    setBusy(true);
    try {
      await api.delete(`/api/dd/deals/${dealId}/requests`);
      setDoc(null);
    } catch (e) {
      setError(e.response?.data?.detail || 'Delete failed.');
    } finally {
      setBusy(false);
    }
  };

  const progress = doc?.progress;

  return (
    <div className="card">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between"
      >
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-lg bg-slate-50">
            <ClipboardList className="w-4 h-4 text-slate-500" />
          </div>
          <div className="text-left">
            <p className="text-sm font-semibold text-navy-900">Counterparty Request List</p>
            <p className="text-xs text-slate-400">
              {doc
                ? `${progress.total} items · ${progress.received_pct}% received · ${progress.reviewed_pct}% reviewed`
                : 'Import the diligence items the other side asked for'}
            </p>
          </div>
        </div>
        {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
      </button>

      {open && (
        <div className="mt-4 space-y-3">
          {error && (
            <div className="flex items-start gap-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-2.5">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {loading ? (
            <div className="flex items-center gap-2 text-xs text-slate-400">
              <Loader2 className="w-4 h-4 animate-spin" />Loading…
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <label className="btn-secondary text-xs py-1.5 px-3 cursor-pointer">
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                  {doc ? 'Re-import (replaces)' : 'Upload request list'}
                  <input
                    type="file"
                    accept=".pdf,.docx,.txt"
                    className="hidden"
                    disabled={busy}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      e.target.value = '';
                      if (f) importList({ file: f });
                    }}
                  />
                </label>
                <button
                  type="button"
                  className="btn-secondary text-xs py-1.5 px-3"
                  onClick={() => setPasteOpen((p) => !p)}
                  disabled={busy}
                >
                  Paste text
                </button>
                {doc && (
                  <button
                    type="button"
                    className="text-xs text-slate-400 hover:text-rose-600 flex items-center gap-1 ml-auto"
                    onClick={remove}
                    disabled={busy}
                  >
                    <Trash2 className="w-3.5 h-3.5" />Remove list
                  </button>
                )}
              </div>

              {pasteOpen && (
                <div className="space-y-2">
                  <textarea
                    rows={6}
                    value={pasteText}
                    onChange={(e) => setPasteText(e.target.value)}
                    placeholder={'1. Projected five-year financial model\n2. Three years audited financials\n…'}
                    className="w-full text-xs border border-slate-300 rounded-lg p-2.5 bg-white text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-navy-500"
                  />
                  <button
                    type="button"
                    className="btn-primary text-xs py-1.5 px-3"
                    disabled={busy || !pasteText.trim()}
                    onClick={() => importList({ text: pasteText })}
                  >
                    Import pasted list
                  </button>
                </div>
              )}

              {doc && (
                <>
                  {doc.source_filename && (
                    <p className="text-xs text-slate-400">
                      Source: {doc.source_filename}
                      {doc.preamble ? ` — “${doc.preamble.slice(0, 140)}${doc.preamble.length > 140 ? '…' : ''}”` : ''}
                    </p>
                  )}

                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-emerald-500 transition-all"
                      style={{ width: `${progress.received_pct}%` }}
                    />
                  </div>

                  <div className="divide-y divide-slate-100 border border-slate-100 rounded-lg">
                    {doc.items.map((it) => {
                      const meta = statusMeta(it.status);
                      const isOpen = expanded === it.id;
                      return (
                        <div key={it.id} className="p-2.5">
                          <div className="flex items-start gap-2">
                            <span className="text-xs text-slate-400 w-5 shrink-0 pt-1.5">{it.number}.</span>
                            <button
                              type="button"
                              className="flex-1 text-left text-xs text-navy-900 leading-snug pt-1.5"
                              onClick={() => {
                                setExpanded(isOpen ? null : it.id);
                                setNoteDraft(it.notes || '');
                              }}
                            >
                              {it.label}
                            </button>
                            <select
                              value={it.status}
                              onChange={(e) => update(it.id, { status: e.target.value })}
                              className={`text-xs rounded-md border px-2 py-1 shrink-0 min-w-[7.5rem] whitespace-nowrap ${meta.cls}`}
                            >
                              {STATUSES.map((s) => (
                                <option key={s.value} value={s.value}>{s.label}</option>
                              ))}
                            </select>
                          </div>

                          {isOpen && (
                            <div className="mt-2 ml-7 space-y-2">
                              {(it.linked_files || []).length > 0 && (
                                <div className="flex flex-wrap gap-1.5">
                                  {it.linked_files.map((lf) => (
                                    <span key={`${lf.room_id}:${lf.filename}`}
                                      className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded-md px-1.5 py-1">
                                      <Link2 className="w-3 h-3" />
                                      <a href={lf.drive_link} target="_blank" rel="noreferrer" className="underline decoration-blue-300">
                                        {lf.filename}
                                      </a>
                                      <button type="button" onClick={() => unlinkFile(it.id, lf)} className="text-blue-400 hover:text-rose-600">
                                        <X className="w-3 h-3" />
                                      </button>
                                    </span>
                                  ))}
                                </div>
                              )}

                              {(room?.suggestions?.[it.id] || []).map((sg) => (
                                <button
                                  key={sg.filename}
                                  type="button"
                                  onClick={() => linkFile(it.id, sg, markReceived && it.status === 'requested')}
                                  className="w-full flex items-center gap-2 text-xs text-navy-900 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-2 hover:bg-amber-100 text-left"
                                >
                                  <Sparkles className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                                  <span className="flex-1">Suggested match: <span className="font-medium">{sg.filename}</span></span>
                                  <span className="text-amber-600">Link it →</span>
                                </button>
                              ))}

                              {room?.files?.length > 0 && (
                                <div>
                                  {attachOpen === it.id ? (
                                    <div className="space-y-1.5">
                                      <select
                                        defaultValue=""
                                        onChange={(e) => e.target.value && linkFile(it.id, JSON.parse(e.target.value), markReceived && it.status === 'requested')}
                                        className="w-full text-xs border border-slate-200 rounded-lg p-2"
                                      >
                                        <option value="">Pick a file from the data room…</option>
                                        {room.files.filter((f) => f.drive_link).map((f) => (
                                          <option key={`${f.room_id}:${f.filename}`} value={JSON.stringify({ room_id: f.room_id, filename: f.filename })}>
                                            {f.filename}{f.linked_items > 0 ? ` (linked to ${f.linked_items} item${f.linked_items > 1 ? 's' : ''})` : ''}
                                          </option>
                                        ))}
                                      </select>
                                      {it.status === 'requested' && (
                                        <label className="flex items-center gap-1.5 text-xs text-slate-500">
                                          <input type="checkbox" checked={markReceived} onChange={(e) => setMarkReceived(e.target.checked)} />
                                          Also mark this item received
                                        </label>
                                      )}
                                      <button type="button" className="text-xs text-slate-400 hover:text-slate-600" onClick={() => setAttachOpen(null)}>
                                        Cancel
                                      </button>
                                    </div>
                                  ) : (
                                    <button
                                      type="button"
                                      className="text-xs text-slate-500 hover:text-navy-900 flex items-center gap-1.5"
                                      onClick={() => setAttachOpen(it.id)}
                                    >
                                      <FolderOpen className="w-3.5 h-3.5" />Attach a data-room file
                                    </button>
                                  )}
                                </div>
                              )}

                              <div className="flex items-center gap-2 text-xs text-slate-500">
                                <span>Analyzed by:</span>
                                <select
                                  value={it.domain}
                                  onChange={(e) => update(it.id, { domain: e.target.value })}
                                  className="text-xs rounded-md border border-slate-200 px-2 py-1 shrink-0 min-w-[7rem] whitespace-nowrap"
                                >
                                  {Object.entries(DOMAIN_LABELS).map(([k, v]) => (
                                    <option key={k} value={k}>{v}</option>
                                  ))}
                                </select>
                                <span className="text-slate-300">(suggested from the item text)</span>
                              </div>
                              <textarea
                                rows={2}
                                value={noteDraft}
                                onChange={(e) => setNoteDraft(e.target.value)}
                                placeholder="Notes (e.g. what was received, what is still missing)"
                                className="w-full text-xs border border-slate-300 rounded-lg p-2 bg-white text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-navy-500"
                              />
                              <button
                                type="button"
                                className="btn-secondary text-xs py-1 px-2.5"
                                onClick={() => update(it.id, { notes: noteDraft })}
                              >
                                Save note
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
